import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n/context';
import { getMap } from '../content/maps';
import { DEFAULT_THEME } from '../content/themes';
import { unitDef } from '../content/units';
import { drawMapBorder, drawTerrain } from '../render/terrain';
import {
    drawBuildings,
    drawMoveTargets,
    drawPlacementGrid,
    drawProjectiles,
    drawSelectionBox,
    drawUnits,
    resetFacings,
} from '../render/entities';
import { Effects } from '../render/effects';
import { buildAtlas } from '../render/atlas';
import { buildCameos } from '../render/cameo';
import { buildTerrainAtlas } from '../render/terrainAtlas';
import { cameraOnTile, clampCamera } from '../render/camera';
import { useCameraControls } from '../render/useCameraControls';
import type { BattlefieldInteraction, ScreenBox, WorldRect } from '../render/useCameraControls';
import { worldToTile } from '../render/projection';
import type { Camera, Viewport } from '../render/projection';
import { placementTiles } from '../render/placement';
import { canPlace, costOf, powerOf, prerequisitesMet, buildQueueOf } from '../sim/build';
import { formatHash, hashState } from '../sim/hash';
import { Phase } from '../sim/types';
import type { ProductionItem, UnitId } from '../sim/types';
import { createNetMatch } from '../game/netMatch';
import type { SessionStatus } from '../net/LockstepSession';
import { BuildPanel } from './BuildPanel';
import type { NetHandoff } from './Lobby';
import type { TranslationKey } from '../i18n/he';

/*
 * מסך הקרב של משחק רשת.
 *
 * **למה זה לא `Battlefield`.** ‏`render/Battlefield.tsx` בונה לעצמו את המנוע
 * בפנים — ‏`LocalCommandSource` ועוד `AiCommandSource` — ואין בו נקודה שאפשר
 * להזריק אליה מנוע קיים. משחק רשת חייב לקבל מנוע **בנוי**, כי ה-`MatchConfig`
 * מגיע מהשרת ומזהה השחקן המקומי אינו קבוע 0.
 *
 * זה מסך רזה בכוונה: אין שמירה אוטומטית (משחק רשת אינו ניתן להמשך לבד), אין
 * מפה מוקטנת ואין אודיו. **ואין השהיה ואין מהירות** — ראו את ההסבר המלא ב-
 * `game/netMatch.ts`: שניהם מתגים מקומיים, ושחקן שמשהה היה מקפיא את היריב.
 *
 * הציור עצמו מורכב מאותן פונקציות של `render/` בדיוק, ולכן אין כאן שכפול של
 * לוגיקת ציור — רק של החיווט. כשיהיה ל-`Battlefield` פרמטר "מנוע מבחוץ", המסך
 * הזה מצטמצם לקריאה אחת אליו.
 */

const MAX_PIXEL_RATIO = 2;

interface NetBattleProps {
    readonly handoff: NetHandoff;
    readonly onExit: () => void;
}

interface NetHud {
    readonly tick: number;
    readonly hash: string;
    readonly selected: number;
    readonly ended: boolean;
    readonly winner: number;
    readonly attackMove: boolean;
    readonly placing: boolean;
    readonly placeError: boolean;
    readonly credits: number;
    readonly power: { produced: number; consumed: number };
    readonly queues: readonly (readonly ProductionItem[])[];
}

/** טקסט הסטטוס של הרשת. `running` הוא היחיד שאין עליו מה לומר. */
function statusKey(status: SessionStatus): TranslationKey | null {
    switch (status.kind) {
        case 'waiting':
            return 'net_status_waiting';
        case 'desync':
            return 'net_status_desync';
        case 'peerLost':
            return 'net_status_peer_lost';
        case 'ended':
            return 'net_status_ended';
        default:
            return null;
    }
}

export function NetBattle({ handoff, onExit }: NetBattleProps) {
    const { t } = useI18n();
    const map = getMap(handoff.mapId);

    /*
     * **המשחק נבנה ב-ref ולא ב-`useMemo`.** ‏`StrictMode` מריץ את גוף הרכיב
     * פעמיים, ו-`useMemo` היה יוצר שני `LockstepSession` שנרשמים לאותה תעבורה —
     * כלומר שני עותקים ששולחים כל תור פעמיים. ‏ref נשמר בין שתי הריצות.
     */
    const matchRef = useRef<ReturnType<typeof createNetMatch> | null>(null);
    if (!matchRef.current) {
        matchRef.current = createNetMatch({
            transport: handoff.transport,
            config: handoff.config,
            localPlayer: handoff.localPlayer,
            mapId: handoff.mapId,
            onStatus: (next) => setStatus(next),
        });
    }
    const match = matchRef.current;
    const me = match.localPlayer;

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const viewportRef = useRef<Viewport>({ width: 0, height: 0 });
    // כמו במסך הראשי: נקודת הפתיחה מגיעה מהמצב, כי היא מוגרלת ולא כתובה במפה.
    const start = match.store.cur.startSlots[me];
    const cameraRef = useRef<Camera>(cameraOnTile(start?.x ?? map.w / 2, start?.y ?? map.h / 2));

    const selectedRef = useRef<Set<UnitId>>(new Set());
    const dragBoxRef = useRef<ScreenBox | null>(null);
    const attackMoveRef = useRef(false);
    const placingRef = useRef(false);
    const placeTilesRef = useRef<number[]>([]);

    const [status, setStatus] = useState<SessionStatus>({ kind: 'running' });
    const [hud, setHud] = useState<NetHud>({
        tick: 0,
        hash: '00000000',
        selected: 0,
        ended: false,
        winner: -1,
        attackMove: false,
        placing: false,
        placeError: false,
        credits: 0,
        power: { produced: 0, consumed: 0 },
        queues: [],
    });

    const mapSize = useMemo(() => ({ w: map.w, h: map.h }), [map.w, map.h]);
    const playerColors = useMemo(
        () => match.store.cur.players.map((_p, i) => DEFAULT_THEME.playerColors[i] ?? '#ffffff'),
        [match],
    );

    const atlasRef = useRef<ReturnType<typeof buildAtlas> | null>(null);
    if (!atlasRef.current) atlasRef.current = buildAtlas(playerColors);

    /** אייקוני הקטלוג בצבע השחקן הזה. ראו `render/cameo.ts`. */
    const cameos = useMemo(() => buildCameos(playerColors[me] ?? '#ffffff'), [playerColors, me]);
    const effectsRef = useRef<Effects | null>(null);
    if (!effectsRef.current) effectsRef.current = new Effects();
    const terrainAtlasRef = useRef<ReturnType<typeof buildTerrainAtlas> | null>(null);
    if (!terrainAtlasRef.current) terrainAtlasRef.current = buildTerrainAtlas();

    /*
     * **היציאה אינה יושבת ב-cleanup של אפקט, וזו החלטה.** ‏`StrictMode` מפרק
     * ומרכיב כל רכיב פעם אחת בפיתוח; ‏`dispose` ב-cleanup היה שולח "עזבתי" ליריב
     * וסוגר את הסוקט מיד עם פתיחת הקרב. לכן הפירוק תלוי בפעולה מפורשת בלבד.
     */
    const leave = useCallback(() => {
        match.dispose('exit');
        onExit();
    }, [match, onExit]);

    const unitAt = useCallback(
        (worldX: number, worldY: number): UnitId | null => {
            let best: UnitId | null = null;
            let bestDistance = Infinity;
            for (const unit of match.store.cur.units) {
                const def = unitDef(unit.type);
                const dx = unit.x - worldX;
                const dy = unit.y - worldY;
                const d2 = dx * dx + dy * dy;
                const reach = def.radius * 1.6;
                if (d2 <= reach * reach && d2 < bestDistance) {
                    bestDistance = d2;
                    best = unit.id;
                }
            }
            return best;
        },
        [match],
    );

    const refreshPlaceTiles = useCallback(() => {
        const head = buildQueueOf(match.store.cur.players[me] ?? { queues: [] })[0];
        placeTilesRef.current =
            placingRef.current && head?.ready
                ? placementTiles(match.store.cur, match.grid, me, head.type, map.w, map.h)
                : [];
    }, [match, me, map.w, map.h]);

    const tryPlace = useCallback(
        (worldX: number, worldY: number): boolean => {
            if (!placingRef.current) return false;
            const tileX = worldToTile(worldX);
            const tileY = worldToTile(worldY);
            const head = buildQueueOf(match.store.cur.players[me] ?? { queues: [] })[0];
            if (!head || !head.ready) return false;
            if (!canPlace(match.store.cur, match.grid, me, head.type, tileX, tileY)) {
                setHud((value) => ({ ...value, placeError: true }));
                return true;
            }
            match.source.issue({ kind: 'place', unitIds: [], tileX, tileY }, match.store.cur.tick);
            placingRef.current = false;
            placeTilesRef.current = [];
            setHud((value) => ({ ...value, placing: false, placeError: false }));
            return true;
        },
        [match, me],
    );

    const issueGround = useCallback(
        (unitIds: UnitId[], worldX: number, worldY: number) => {
            const kind = attackMoveRef.current ? 'attackMove' : 'move';
            attackMoveRef.current = false;
            match.source.issue(
                { kind, unitIds, tileX: worldToTile(worldX), tileY: worldToTile(worldY) },
                match.store.cur.tick,
            );
        },
        [match],
    );

    const interaction = useMemo<BattlefieldInteraction>(
        () => ({
            onBoxSelect: (rect: WorldRect, additive: boolean) => {
                const next = additive ? new Set(selectedRef.current) : new Set<UnitId>();
                for (const unit of match.store.cur.units) {
                    if (unit.owner !== me) continue;
                    if (
                        unit.x >= rect.minX &&
                        unit.x <= rect.maxX &&
                        unit.y >= rect.minY &&
                        unit.y <= rect.maxY
                    ) {
                        next.add(unit.id);
                    }
                }
                selectedRef.current = next;
            },
            onTap: (worldX: number, worldY: number, isTouch: boolean) => {
                if (tryPlace(worldX, worldY)) return;
                const id = unitAt(worldX, worldY);
                const unit = id === null ? null : match.store.cur.units.find((u) => u.id === id);
                const unitIds = [...selectedRef.current].sort((a, b) => a - b);

                if (unit && unit.owner === me) {
                    selectedRef.current = new Set([unit.id]);
                    return;
                }
                if (unit && unitIds.length > 0) {
                    match.source.issue(
                        { kind: 'attack', unitIds, targetId: unit.id },
                        match.store.cur.tick,
                    );
                    return;
                }
                // במגע אין כפתור ימני, ולכן הקשה על קרקע ריקה חייבת להיות פקודה.
                if (isTouch && unitIds.length > 0) {
                    issueGround(unitIds, worldX, worldY);
                    return;
                }
                selectedRef.current = new Set();
            },
            onCommand: (worldX: number, worldY: number) => {
                if (tryPlace(worldX, worldY)) return;
                if (selectedRef.current.size === 0) return;
                const unitIds = [...selectedRef.current].sort((a, b) => a - b);
                const hitId = unitAt(worldX, worldY);
                const hit = hitId === null ? null : match.store.cur.units.find((u) => u.id === hitId);
                if (hit && hit.owner !== me) {
                    match.source.issue(
                        { kind: 'attack', unitIds, targetId: hit.id },
                        match.store.cur.tick,
                    );
                    return;
                }
                issueGround(unitIds, worldX, worldY);
            },
            onDragBox: (box: ScreenBox | null) => {
                dragBoxRef.current = box;
            },
        }),
        [match, me, unitAt, tryPlace, issueGround],
    );

    const controls = useCameraControls(containerRef, cameraRef, viewportRef, mapSize, interaction);

    // ציור + סימולציה. אין כאן מקש רווח: השהיה אינה קיימת במשחק רשת.
    useEffect(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        const atlas = atlasRef.current;
        const effects = effectsRef.current;
        const terrainAtlas = terrainAtlasRef.current;
        if (!container || !canvas || !atlas || !effects || !terrainAtlas) return;

        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) return;

        resetFacings();
        const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);

        const resize = () => {
            const width = container.clientWidth;
            const height = container.clientHeight;
            if (width === 0 || height === 0) return;
            viewportRef.current = { width, height };
            canvas.width = Math.round(width * ratio);
            canvas.height = Math.round(height * ratio);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            cameraRef.current = clampCamera(cameraRef.current, viewportRef.current, mapSize);
        };

        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(container);

        let frame = 0;
        let last = performance.now();

        const draw = (now: number) => {
            const delta = Math.min(now - last, 250);
            last = now;

            controls.applyKeyboardPan(delta);
            /*
             * ‏`advance` מקדם זמן, אבל `NetworkCommandSource.ready` חוסם כל טיק
             * שהפקודות שלו טרם הגיעו — ולכן הצד המהיר ממתין לאיטי מעצמו, בלי
             * שום סנכרון שעונים. זה כל מה ש-lockstep צריך מהלולאה.
             */
            match.loop.advance(delta);

            const camera = cameraRef.current;
            const viewport = viewportRef.current;
            const state = match.store.cur;

            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            ctx.fillStyle = DEFAULT_THEME.colors.bg;
            ctx.fillRect(0, 0, viewport.width, viewport.height);

            drawTerrain(
                ctx,
                { w: map.w, h: map.h, terrain: map.terrain, ore: state.ore },
                camera,
                viewport,
                terrainAtlas,
            );
            drawMapBorder(ctx, map, camera, viewport, DEFAULT_THEME.colors.border);
            effects.consume(state.events);
            effects.update(delta);
            effects.drawGround(ctx, camera, viewport);
            drawPlacementGrid(
                ctx,
                camera,
                viewport,
                placeTilesRef.current,
                map.w,
                DEFAULT_THEME.colors.success,
            );
            drawBuildings(ctx, camera, viewport, state, playerColors);
            drawMoveTargets(ctx, camera, viewport, state, selectedRef.current, map.w);
            drawUnits(ctx, camera, viewport, {
                cur: state,
                prev: match.store.prev,
                alpha: match.loop.alpha,
                atlas,
                playerColors,
                selected: selectedRef.current,
                localPlayer: me,
            });
            drawProjectiles(
                ctx,
                camera,
                viewport,
                state,
                match.store.prev,
                match.loop.alpha,
                playerColors,
            );
            effects.drawAir(ctx, camera, viewport);
            if (dragBoxRef.current) drawSelectionBox(ctx, dragBoxRef.current);

            frame = requestAnimationFrame(draw);
        };

        frame = requestAnimationFrame(draw);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [match, map, mapSize, me, playerColors, controls]);

    // רענון ה-HUD ב-4Hz, מחוץ ללולאת הציור — בדיוק כמו במשחק היחיד.
    useEffect(() => {
        const id = setInterval(() => {
            const state = match.store.cur;
            if (placingRef.current) refreshPlaceTiles();
            setHud((value) => ({
                ...value,
                tick: state.tick,
                hash: formatHash(hashState(state)),
                selected: selectedRef.current.size,
                ended: state.phase === Phase.Ended,
                winner: state.winner,
                attackMove: attackMoveRef.current,
                placing: placingRef.current,
                credits: state.players[me]?.credits ?? 0,
                power: powerOf(state.buildings, me),
                queues: state.players[me]?.queues ?? [],
            }));
        }, 250);
        return () => clearInterval(id);
    }, [match, me, refreshPlaceTiles]);

    const netKey = statusKey(status);
    /*
     * ‏desync ו-peerLost הם סוף המשחק בפועל: מכאן זה כבר לא אותו משחק בשני
     * הצדדים, והצגת "המשך לשחק" הייתה שקר. מציגים את אותה חלונית של סיום.
     */
    const halted = status.kind === 'desync' || status.kind === 'peerLost';

    return (
        <div className="game">
            <div className="game__canvasWrap" ref={containerRef} dir="ltr">
                <canvas ref={canvasRef} className="game__canvas" />

                <div className="game__overlay" dir="auto">
                    <span className="game__mapName">{t(map.def.nameKey)}</span>
                    <span
                        className={
                            hud.placeError ? 'game__status game__status--error' : 'game__status'
                        }
                    >
                        {hud.placing
                            ? hud.placeError
                                ? t('build_hint_bad_spot')
                                : t('build_hint_place')
                            : (hud.queues[0]?.[0]?.ready ?? false)
                              ? t('build_hint_ready')
                              : hud.selected > 0
                                ? `${t('game_selected')} ${hud.selected} ${t('game_units')}`
                                : t('game_hint_select')}
                    </span>
                    {/*
                        מצב הרשת יושב תמיד על המסך ולא בפאנל דיבאג: "ממתין ליריב"
                        נראה בדיוק כמו משחק תקוע, וזו השורה שמסבירה את ההבדל.
                    */}
                    <span className="game__status game__netStatus">
                        <span className="num">{handoff.room}</span> ·{' '}
                        {t('net_player_label')} <span className="num">{me + 1}</span>
                        {netKey ? ` · ${t(netKey)}` : ''}
                    </span>
                </div>

                <BuildPanel
                    credits={hud.credits}
                    power={hud.power}
                    queues={hud.queues}
                    cameos={cameos}
                    onExplainPower={() => {}}
                    starved={false}
                    canAfford={(kind, type) => hud.credits >= costOf({ kind, type })}
                    available={(kind, type) =>
                        prerequisitesMet({ kind, type }, match.store.cur.buildings, me)
                    }
                    onQueue={(kind, type) =>
                        match.source.issue(
                            { kind: 'queue', unitIds: [], itemKind: kind, itemType: type },
                            match.store.cur.tick,
                        )
                    }
                    onCancel={(kind: number) =>
                        match.source.issue(
                            { kind: 'cancel', unitIds: [], queueIndex: kind },
                            match.store.cur.tick,
                        )
                    }
                    rallying={false}
                    onRally={() => {}}
                    placing={hud.placing}
                    onPlace={() => {
                        placingRef.current = !placingRef.current;
                        refreshPlaceTiles();
                        setHud((value) => ({
                            ...value,
                            placing: placingRef.current,
                            placeError: false,
                        }));
                    }}
                />

                {(hud.ended || halted) && (
                    <div className="game__result" role="status">
                        <div
                            className={
                                hud.ended && hud.winner === me
                                    ? 'game__resultTitle game__resultTitle--win'
                                    : 'game__resultTitle'
                            }
                        >
                            {hud.ended
                                ? hud.winner === me
                                    ? t('result_victory')
                                    : t('result_defeat')
                                : netKey
                                  ? t(netKey)
                                  : t('net_status_ended')}
                        </div>
                        <button className="game__button" type="button" onClick={leave}>
                            {t('result_back')}
                        </button>
                    </div>
                )}
            </div>

            <div className="game__hud">
                <div className="game__group">
                    <button className="game__button" type="button" onClick={leave}>
                        {t('net_leave')}
                    </button>
                </div>

                <div className="game__group game__group--selection" aria-disabled={hud.selected === 0}>
                    <button
                        className="game__button game__button--clear"
                        type="button"
                        disabled={hud.selected === 0}
                        onClick={() => {
                            selectedRef.current = new Set();
                            setHud((value) => ({ ...value, selected: 0 }));
                        }}
                    >
                        {t('game_deselect')}
                    </button>
                    <button
                        className={
                            hud.attackMove ? 'game__button game__button--active' : 'game__button'
                        }
                        type="button"
                        disabled={hud.selected === 0}
                        aria-pressed={hud.attackMove}
                        onClick={() => {
                            attackMoveRef.current = true;
                            setHud((value) => ({ ...value, attackMove: true }));
                        }}
                    >
                        {t('game_attack_move')}
                    </button>
                    <button
                        className="game__button"
                        type="button"
                        disabled={hud.selected === 0}
                        onClick={() =>
                            match.source.issue(
                                { kind: 'stop', unitIds: [...selectedRef.current].sort((a, b) => a - b) },
                                match.store.cur.tick,
                            )
                        }
                    >
                        {t('game_stop')}
                    </button>
                </div>

                <span className="game__spacer" />

                {/*
                    **אין כאן כפתור השהיה ואין בורר מהירות, וזה מכוון.** שניהם
                    מתגים מקומיים: מי שמשהה מפסיק לשדר "אין לי פקודות", והיריב
                    קופא עד שמוכרז אבוד. ההסבר המלא ב-`game/netMatch.ts`.
                */}
                <div className="game__group">
                    <span className="game__netHint num">{hud.hash}</span>
                </div>
            </div>
        </div>
    );
}
